import { useCart } from '../../../hooks/useCart';
import React from 'react';
import { ShippingIcon } from './ShippingIcon';
import Money from '../../Money';

type MiniCartFreeShippingBarProps = {
  targetAmount: number;
  beforeTargetText: string;
  afterTargetText: string;
  automaticFreeShippingSkus: string;
};

export function MiniCartFreeShippingBar({
  targetAmount,
  beforeTargetText,
  afterTargetText,
  automaticFreeShippingSkus = '',
}: MiniCartFreeShippingBarProps) {
  const {
    cart: { items },
  } = useCart();
  const parts = beforeTargetText?.split('REMAINING') || [];

  const qualifyingSpendForFreeShipping = items.reduce((acc, item) => {
    if (item.gift_card) return acc;
    if ('selling_plan_allocation' in item) {
      return (
        acc + item.selling_plan_allocation.compare_at_price * item.quantity
      );
    }
    return acc + item.original_line_price;
  }, 0);

  const automaticFreeShippingSkusArray = automaticFreeShippingSkus
    .split(',')
    .map((sku) => sku.trim())
    .filter((sku) => sku !== '');

  const qualifiesForAutomaticFreeShipping = items.some((item) => {
    return automaticFreeShippingSkusArray.includes(item.sku);
  });

  // Free shipping if:
  const qualifiesForFreeShipping = items.length > 0 && (
    // - All items are gift cards
    items.every((item) => item.gift_card) ||
    // - The customer has a product that qualifies for automatic free shipping
    qualifiesForAutomaticFreeShipping ||
    // - The customer has spent enough money excluding gift cards
    qualifyingSpendForFreeShipping >= targetAmount);

  return (
    <div className="minicart-free-shipping-bar">
      <div className="minicart-free-shipping-bar-text">
        {qualifiesForFreeShipping ? (
          <span dangerouslySetInnerHTML={{ __html: afterTargetText }} />
        ) : (
          <>
            <ShippingIcon />
            <span>
              {parts.map((part, i) => {
                return (
                  <React.Fragment key={i}>
                    {i === 1 && (
                      <span className="bold">
                        <Money
                          amount={targetAmount - qualifyingSpendForFreeShipping}
                        />
                      </span>
                    )}
                    <span dangerouslySetInnerHTML={{ __html: part }} />
                  </React.Fragment>
                );
              })}
            </span>
          </>
        )}
      </div>
      <div className="minicart-free-shipping-bar-progress-bar">
        <div
          style={
            {
              '--progress-bar-width': `${
                Math.min(
                  qualifiesForFreeShipping
                    ? 1
                    : qualifyingSpendForFreeShipping / targetAmount,
                  1
                ) * 100
              }%`,
            } as React.CSSProperties
          }
          className="minicart-free-shipping-bar-progress-bar-fill"
        ></div>
      </div>
    </div>
  );
}
